import type { Image, ToolMark, WorkChapter, WorkItem } from './schema'
import { site } from './site'
import { method } from './method'
import { tools } from './tools'
import {
  aiAutomations,
  countBuilds,
  contentCreation,
  featuredProject,
  integrations,
  leadManagement,
  maintained,
  platforms,
  roofingProject,
  supportWorkflows,
  workCount,
  type Build,
  type ListedBuild,
} from './work'

/**
 * Home page copy. The hero pairs a thin line with a bold one. The bold line is sized in `cqi` in
 * features/home/hero/hero.css so it fills the column: re-measure it when you change the words.
 */
export const homeHero = {
  headlineThin: 'Automations that',
  headlineBold: 'Hours back, every week.',
  subhead: 'I connect your tools, automate the repetitive work, and add AI where it pays — in n8n, Make.com, Zapier and the apps you already use.',
  cta: { label: 'Get in touch', to: '/#contact' },
}

/**
 * The statement under the hero: words light up as the page scrolls, each keyword lands bold and
 * moves the tracker. Replace the words, keep four keywords (the tracker shows them in order).
 * A string is plain words; `{ key }` is a keyword.
 */
export type ManifestoPart = string | { key: string }

export const homeManifesto = {
  eyebrow: method.eyebrow,
  parts: [
    'I connect your',
    { key: 'tools,' },
    'automate the',
    { key: 'busywork,' },
    'and add',
    { key: 'AI' },
    'where it',
    { key: 'pays.' },
  ] satisfies ManifestoPart[],
}

export const homeSections = {
  work: {
    eyebrow: 'Selected work',
    title: 'Builds you can open right now.',
  },
  proof: {
    eyebrow: 'Proof',
    title: 'What I run for clients today.',
  },
  objections: {
    eyebrow: 'Before you write',
    title: 'The three questions everyone asks.',
  },
}

export const ctaBand = {
  title: 'Tell me what is eating your week.',
  body: 'Describe the task that keeps repeating. I will map it and tell you honestly what automation can take over — and what it saves you.',
  button: 'Email me your question',
}

/* ---------- Work section ---------- */

/** Real pixel sizes of the prepared WebP copies in /images/work (public/). */
const dims: Record<string, [number, number]> = {
  'support-ticketing': [1600, 900],
  'support-ticketing-workflows': [1600, 2218],
  'roofing-voice-ai': [1600, 900],
  'roofing-workflows': [1600, 612],
  'roofing-doc': [1200, 848],
  'rag-production': [1600, 900],
  'rag-production-workflow': [1600, 1017],
  'rag-production-telegram': [1200, 1331],
  'sales-ai-agent': [1600, 1035],
  'ai-assistant': [1600, 1035],
  'rag-chatbot': [1600, 1035],
  'invoice-processing': [1600, 1035],
  'gmail-integration': [1600, 1035],
  'asana-xero': [1600, 1035],
  'ghl-automation': [1365, 633],
  'leads-enrichment': [1600, 1035],
  'leads-action': [1600, 1035],
  'google-maps-scraper': [1600, 1035],
  'content-repurposing': [1600, 1035],
  'social-media': [1600, 1035],
  'youtube-shorts': [1600, 1035],
  'viral-videos': [1600, 1035],
  'support-wf-062351': [1176, 1479],
  'support-wf-062506': [1176, 1479],
  'support-wf-062554': [770, 1920],
  'support-wf-062635': [820, 1920],
  'support-wf-062749': [1282, 1920],
  'support-wf-062826': [706, 1280],
}

const img = (slug: string, alt: string): Image => {
  const [width, height] = dims[slug] ?? [1600, 1035]
  return { src: `/images/work/${slug}.webp`, alt, width, height }
}

const items = (kicker: string, group: string, builds: Build[]): WorkItem[] =>
  builds.map((b) => ({
    id: b.id,
    name: b.title,
    kicker: b.kicker ?? kicker,
    group: b.group ?? group,
    summary: b.summary,
    ...(b.image ? { image: img(b.image, b.alt ?? b.title) } : {}),
  }))

const listed = (kicker: string, builds: ListedBuild[]): WorkItem[] =>
  builds.map((b, i) => ({
    id: `${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${i + 1}`,
    name: b.name,
    kicker,
    summary: b.summary,
    stack: b.stack,
  }))

/** The URL of a chapter's showcase sheet. Real, shareable and in the sitemap. */
export const chapterPath = (id: string) => `/work/${id}`

export const workSection = {
  ...homeSections.work,
  /** The title as one display line in the hero's thin + bold pairing. */
  titleThin: 'Builds you can',
  titleBold: 'open right now.',
  all: { label: `See all ${workCount} builds`, to: '/work' },
}

/**
 * The Work chapters, in grid order. Add, remove or reorder a chapter here; the grid, the sheets and
 * the sitemap follow. A chapter with a
 * `lead` is a single build; one with no landscape picture shows its `marks` as a logo face instead.
 */
export const workChapters: WorkChapter[] = [
  {
    id: 'featured',
    title: 'Support Ticketing System',
    line: 'A GoHighLevel system that never loses a support request.',
    count: 1,
    unit: 'system',
    description: 'A full customer-support pipeline inside GoHighLevel: intake, categories, stages, follow-ups and a reopen window.',
    marks: [tools.gohighlevel],
    items: [],
    lead: img('support-ticketing', 'Support Ticketing System infographic'),
    gallery: featuredProject.gallery,
  },
  {
    id: 'roofing-voice-ai',
    title: roofingProject.title,
    line: 'Riley, an AI voice receptionist, plus the full roofing sales pipeline.',
    count: 1,
    unit: 'project',
    description: 'A GoHighLevel voice AI and pipeline for a roofing company: every caller captured, qualified, followed up and booked.',
    marks: [tools.gohighlevel],
    items: [],
    lead: roofingProject.lead,
    gallery: roofingProject.gallery,
    story: roofingProject.story,
  },
  {
    id: 'case-studies',
    title: 'AI Automations',
    line: 'AI agents that handle the repetitive work.',
    count: countBuilds(aiAutomations),
    unit: 'builds',
    description: 'AI agents and RAG chatbots built in n8n with OpenAI, Gemini, Supabase, Cohere and Pinecone.',
    marks: [tools.openai, tools.gemini, tools.n8n],
    items: items('AI agent', 'AI Automations', aiAutomations),
  },
  {
    id: 'process',
    title: 'Integrations',
    line: 'Apps that finally talk to each other.',
    count: countBuilds(integrations),
    unit: 'integrations',
    description: 'GoHighLevel CRM pipelines, plus Gmail, Asana and Xero connected into one smooth flow.',
    marks: [tools.make, tools.asana, tools.gohighlevel],
    items: items('Integration', 'Integrations', integrations),
  },
  {
    id: 'screens',
    title: 'Lead Management',
    line: 'Every lead captured, enriched and followed up.',
    count: leadManagement.length,
    unit: 'flows',
    description: 'Lead enrichment, task-based follow-up and scraping pipelines for prospecting.',
    marks: [tools.zapier, tools.asana, tools.airtable],
    items: items('Lead flow', 'Lead Management', leadManagement),
  },
  {
    id: 'websites',
    title: 'Content Creation',
    line: 'One source file, content everywhere.',
    count: contentCreation.length,
    unit: 'pipelines',
    description: 'Content repurposing, social posts, YouTube Shorts and AI video generation.',
    marks: [tools.n8n, tools.gemini, tools.elevenlabs],
    items: items('Content pipeline', 'Content Creation', contentCreation),
  },
  {
    id: 'apps',
    title: 'Platforms I Build In',
    line: 'Three engines, one outcome: less manual work.',
    count: platforms.length,
    unit: 'platforms',
    description: 'n8n, Make.com and Zapier — the three platforms my workflows run on.',
    marks: [tools.n8n, tools.zapier, tools.make],
    items: listed('Platform', platforms),
  },
  {
    id: 'side-projects',
    title: 'GoHighLevel Workflows',
    line: 'Inside the Support Ticketing System, stage by stage.',
    count: supportWorkflows.length,
    unit: 'workflows',
    description: 'The stage-by-stage GHL workflows: urgent, in progress, awaiting reply, resolved, replies and reopen.',
    marks: [tools.gohighlevel],
    tile: img('support-ticketing-workflows', 'Full workflow breakdown of the Support Ticketing System'),
    items: supportWorkflows.map((w) => ({
      id: w.id,
      name: w.name,
      kicker: 'GHL workflow',
      group: 'Support Ticketing System',
      summary: w.summary,
      image: img(w.image!, w.alt ?? w.name),
    })),
  },
  {
    id: 'experiments',
    title: 'Automations I Keep Running',
    line: 'The background jobs that never miss.',
    count: maintained.length,
    unit: 'systems',
    description: 'Emails, CRM updates, scheduling and documentation — maintained so nothing drifts.',
    marks: [tools.make, tools.gohighlevel, tools.slack],
    items: listed('Running system', maintained),
  },
]

/** The site's own mark for chapter logo faces. */
export const siteMark: ToolMark = { name: site.brand, logo: site.logo.light }

export const findChapter = (id: string | undefined) => workChapters.find((chapter) => chapter.id === id)
